import core = egr.wcf.core;
import utils = egr.wcf.utils;

type setTextureOverride = (name: string, texture: BABYLON.Texture | null) => BABYLON.ShaderMaterial;

export class FloorPlan {
    public get planeYOffset(): number {
        return (this.mPlane.position.y);
    }

    public set planeYOffset(pOffset: number) {
        this.mPlane.position.y = pOffset;
        utils.mesh.updateFrozenWorldMatrix(this.mPlane);
    }

    public get enabled(): boolean {
        return (this.mPlane.isEnabled());
    }

    public set enabled(pEnabled: boolean) {
        this.mPlane.setEnabled(pEnabled);
    }

    public get visibility(): number {
        return (this.mVisibility);
    }

    public set visibility(pVisibility: number) {
        this.mVisibility = pVisibility;
        this.mMaterial.setFloat('visibility', this.mVisibility);
    }

    /** Get current color reduction value. */
    public get colorReduction(): number {
        // map value from [1, 10] to [0, 1]
        const tValue: number = Math.sqrt((this.mColorReduction - 1.0) / 9.0);
        return (tValue);
    }
    /**
     * Set color reduction value. (Range: [0.0, 1.0])
     * 0.0 = full color, 1.0 = no color
     */
    public set colorReduction(pReduction: number) {
        // map value from [0, 1] to [1, 10]
        const tValue: number = 1.0 + (pReduction * pReduction) * 9.0;
        this.mColorReduction = tValue;
        this.mMaterial.setFloat('colorReduction', this.mColorReduction);
    }

    public get brightness(): number {
        return (this.mBrightness);
    }

    public set brightness(pBrightness: number) {
        this.mBrightness = pBrightness;
        this.mMaterial.setFloat('brightness', this.mBrightness);
    }

    public get placingAreaMin(): BABYLON.Vector2 {
        return (this.mPlacingAreaMin);
    }

    public get placingAreaMax(): BABYLON.Vector2 {
        return (this.mPlacingAreaMax);
    }
    public static MAXSIDELENGTH: number = 2048;
    //// private ///////////////////////////////////////////////////////////////////////////////
    private static MIN_NAVIGATION_RADIUS: number = 20;
    private static MIN_PLACING_AREA: number = 5;

    private mCoreApp: core.Application;
    private mPlane: BABYLON.Mesh;
    private mMaterial: BABYLON.ShaderMaterial;
    private mTexture: BABYLON.Texture | null;
    private mPlacingAreaMin: BABYLON.Vector2;
    private mPlacingAreaMax: BABYLON.Vector2;
    private mColorReduction: number; // range [1.0, 10.0];
    private mBrightness: number; // range [0.0, 1.0];
    private mVisibility: number; // range [0.0, 1.0];

    public constructor(pCoreApp: core.Application) {
        this.mCoreApp = pCoreApp;
        this.mTexture = null;
        this.mPlacingAreaMin = new BABYLON.Vector2(-FloorPlan.MIN_PLACING_AREA, -FloorPlan.MIN_PLACING_AREA);
        this.mPlacingAreaMax = new BABYLON.Vector2(FloorPlan.MIN_PLACING_AREA, FloorPlan.MIN_PLACING_AREA);
        this.mColorReduction = 1.5;
        this.mBrightness = 1.0;
        this.mVisibility = 0.5;

        this.mPlane = BABYLON.MeshBuilder.CreateGround(
            'FloorPlan',
            { width: 1.0, height: 1.0 },
            this.mCoreApp.model.scene
        );
        this.mPlane.isPickable = false;
        this.mPlane.isVisible = false;
        this.mPlane.position.y = -0.006;
        this.mPlane.freezeWorldMatrix();

        this.mMaterial = new BABYLON.ShaderMaterial(
            'FloorPlan',
            this.mCoreApp.model.scene,
            'wcf.FloorPlan',
            {
                attributes: ['position', 'uv'],
                uniforms: ['worldViewProjection', 'texelSize', 'visibility', 'colorReduction', 'brightness']
            }
        );
        this.mMaterial.setFloat('visibility', this.mPlane.visibility);
        this.mMaterial.setFloat('colorReduction', this.mColorReduction);
        this.mMaterial.setFloat('brightness', this.mBrightness);
        this.mPlane.material = this.mMaterial;
    }

    public hasTexture(): boolean {
        return this.mTexture != null;
    }

    public dispose(): void {
        this.mPlane.dispose();
        this.mMaterial.dispose();
        if (this.mTexture != null) {
            this.mTexture.dispose();
        }
    }

    public setTexture(pUrl: string): void {
        // FixMe: check if this is correct
        (this.mMaterial.setTexture as setTextureOverride)('textureSampler', null);
        if (this.mTexture != null) {
            this.mTexture.dispose();
            this.mTexture = null;
        }
        this.mPlane.isVisible = false;

        if (utils.string.isNullOrEmpty(pUrl)) {
            return;
        }

        const tScene: BABYLON.Scene = this.mCoreApp.model.scene;
        if (utils.string.isDataUri(pUrl)) {
            this.mTexture = BABYLON.Texture.CreateFromBase64String(
                pUrl,
                'wcf.FloorPlanTexture',
                tScene,
                false,
                true,
                BABYLON.Texture.TRILINEAR_SAMPLINGMODE,
                this.onTextureLoaded
            );
        } else {
            this.mTexture = new BABYLON.Texture(
                pUrl,
                tScene,
                false,
                true,
                BABYLON.Texture.TRILINEAR_SAMPLINGMODE,
                this.onTextureLoaded
            );
        }
        this.mTexture.anisotropicFilteringLevel = tScene.getEngine().getCaps().maxAnisotropy;
    }

    /** Set size of the floor plan (meter) */
    public setSize(pSizeX: number, pSizeY: number, pZoomToFit: boolean = true): void {
        // update plane size
        this.mPlane.scaling.x = pSizeX;
        this.mPlane.scaling.z = pSizeY;
        utils.mesh.updateFrozenWorldMatrix(this.mPlane);

        // update camera navigation area
        let tRadius: number = Math.max(pSizeX, pSizeY) * 0.5;
        tRadius += 10.0; // add 10 meter offset
        tRadius = Math.max(tRadius, FloorPlan.MIN_NAVIGATION_RADIUS); // limit to minimum radius
        for (const tView of this.mCoreApp.viewer.views) {
            tView.cameraControl.setNavigationArea(tRadius);
        }

        // update element placement area
        const tAreaX: number = Math.max(pSizeX * 0.5, FloorPlan.MIN_PLACING_AREA);
        const tAreaY: number = Math.max(pSizeY * 0.5, FloorPlan.MIN_PLACING_AREA);
        this.mPlacingAreaMin = new BABYLON.Vector2(-tAreaX, -tAreaY);
        this.mPlacingAreaMax = new BABYLON.Vector2(tAreaX, tAreaY);

        if (pZoomToFit) {
            // force plane to be visible and enabled, zoomToFit ignores invisible/deactivated objects
            const tWasVisible: boolean = this.mPlane.isVisible;
            const tWasEnabled: boolean = this.enabled;
            this.mPlane.isVisible = true;
            this.enabled = true;

            if (this.mCoreApp.viewer.activeView != null) {
                this.mCoreApp.viewer.activeView.cameraControl.zoomToFitMeshes([this.mPlane]);
            }

            this.mPlane.isVisible = tWasVisible;
            this.enabled = tWasEnabled;
            if (tWasVisible && tWasEnabled) {
                this.mCoreApp.viewer.requestRenderFrame();
            }
        }
    }

    private onTextureLoaded = (): void => {
        if (this.mTexture != null) {
            const tSize: BABYLON.ISize = this.mTexture.getSize();
            const tTexelSize: BABYLON.Vector2 = new BABYLON.Vector2(1.0 / tSize.width, 1.0 / tSize.height);
            this.mMaterial.setVector2('texelSize', tTexelSize);
            this.mMaterial.setTexture('textureSampler', this.mTexture);
        }

        this.mPlane.isVisible = true;
        this.mCoreApp.viewer.requestRenderFrame();
    };
}

// register shader
BABYLON.Effect.ShadersStore['wcf.FloorPlanVertexShader'] = `
precision highp float;

attribute vec3 position;
attribute vec2 uv;
uniform mat4 worldViewProjection;

varying vec2 vUV;

void main(void)
{
        gl_Position = worldViewProjection * vec4(position, 1.0);
        vUV = uv;
}`;

BABYLON.Effect.ShadersStore['wcf.FloorPlanFragmentShader'] = `
precision highp float;

varying vec2 vUV;
uniform sampler2D textureSampler;
uniform vec2 texelSize;
uniform float visibility;
uniform float colorReduction;
uniform float brightness;

void main(void)
{
    vec2 off1 = texelSize * 1.0;
    vec2 off2 = texelSize * 2.0;

    vec3 color = texture2D(textureSampler, vUV).rgb;
    vec3 color1 = texture2D(textureSampler, vUV + vec2(off1.x, 0.0)).rgb;
    vec3 color2 = texture2D(textureSampler, vUV + vec2(-off1.x, 0.0)).rgb;
    vec3 color3 = texture2D(textureSampler, vUV + vec2(0.0, off1.y)).rgb;
    vec3 color4 = texture2D(textureSampler, vUV + vec2(0.0, -off1.y)).rgb;

    vec3 color5 = texture2D(textureSampler, vUV + vec2(off2.x, off2.y)).rgb;
    vec3 color6 = texture2D(textureSampler, vUV + vec2(-off2.x, off2.y)).rgb;
    vec3 color7 = texture2D(textureSampler, vUV + vec2(off2.x, -off2.y)).rgb;
    vec3 color8 = texture2D(textureSampler, vUV + vec2(-off2.x, -off2.y)).rgb;

    float lum = 0.299 * color.r + 0.587 * color.g + 0.114 * color.b;
    float lum1 = 0.299 * color1.r + 0.587 * color1.g + 0.114 * color1.b;
    float lum2 = 0.299 * color2.r + 0.587 * color2.g + 0.114 * color2.b;
    float lum3 = 0.299 * color3.r + 0.587 * color3.g + 0.114 * color3.b;
    float lum4 = 0.299 * color4.r + 0.587 * color4.g + 0.114 * color4.b;

    float lum5 = 0.299 * color5.r + 0.587 * color5.g + 0.114 * color5.b;
    float lum6 = 0.299 * color6.r + 0.587 * color6.g + 0.114 * color6.b;
    float lum7 = 0.299 * color7.r + 0.587 * color7.g + 0.114 * color7.b;
    float lum8 = 0.299 * color8.r + 0.587 * color8.g + 0.114 * color8.b;

    float lumNear = (lum1 + lum2 + lum3 + lum4 + lum5 + lum6 + lum7 + lum8) / 8.0;
    float lumDiff =  lumNear - lum;

    if (lum < 0.25 || lumDiff > 0.05) {
        color = clamp(pow(color, vec3(3.0)), vec3(0.0), vec3(1.0));
    } else {
        color = clamp(color * vec3(colorReduction), vec3(0.0), vec3(1.0));
    }
    color = color * visibility + (vec3(1.0, 1.0, 1.0) * (1.0 - visibility));
    color *= brightness;

    gl_FragColor = vec4(color.r, color.g, color.b, 1.0);
}`;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/FloorPlan.ts